import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import EditCardForm from 'containers/PaymentMethodManagement/EditCardForm';
import { http } from 'middleware/apiMiddleware';
import { useCallback, useEffect, useState } from 'react';
import CustomerCradDetails from 'containers/PaymentMethodManagement/CustomerCradDetails';
import Render from 'components/Common/Render';
import { Loader } from 'components/Common/Loader';
import GoBack from 'components/Common/GoBack';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { RootState } from 'store';
import ModalComponent from 'components/Common/Modal';
import { useLazyDefaultAccountQuery } from 'store/apis/defaultAccountApi';
import { setDefaultAccount } from 'store/slices/defaultAccountSlice';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY || '');

const CardManagement = () => {
  const selectedBusiness = useAppSelector((state: RootState) => state.businesses.business);

  const [
    fetchDefaultAccount,
    { data: defaultPaymentMethod, isFetching: isFetchingCustomerDetails, isSuccess },
  ] = useLazyDefaultAccountQuery({});
  const dispatch = useAppDispatch();
  const [clientSecret, setClientSecret] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);

  const fetchClientSecret = useCallback(async () => {
    try {
      setIsCreatingIntent(true);
      // Create card setup intent
      const { data, error } = await http.get(
        `/business/${selectedBusiness?.uuid}/card-setup-intent`,
      );
      if (!!data && !error) setClientSecret(data?.setupIntentClientSecret);
    } catch (error) {
      // Error activating stripe module
    } finally {
      setIsCreatingIntent(false);
    }
  }, []);

  const onEdit = () => {
    setClientSecret('');
    setIsEdit(true);
    fetchClientSecret();
  };

  const onCancel = () => {
    setIsEdit(false);
    setClientSecret('');
  };

  const onSuccess = () => {
    setIsEdit(false);
    setClientSecret('');
    if (selectedBusiness?.uuid) {
      fetchDefaultAccount({ uuid: selectedBusiness.uuid });
    }
  };

  useEffect(() => {
    if (selectedBusiness?.uuid) {
      fetchDefaultAccount({ uuid: selectedBusiness.uuid });
    }
  }, []);

  useEffect(() => {
    if (defaultPaymentMethod && isSuccess) {
      dispatch(setDefaultAccount(defaultPaymentMethod));
    }
  }, [defaultPaymentMethod, isSuccess]);

  return (
    <div>
      <GoBack label="Payment Details" />
      <Render when={isFetchingCustomerDetails}>
        <div className="flex justify-center w-full">
          <Loader />
        </div>
      </Render>
      <Render when={!!defaultPaymentMethod && !isFetchingCustomerDetails}>
        <CustomerCradDetails defaultPaymentMethod={defaultPaymentMethod} onEdit={onEdit} />
      </Render>
      <Render when={(!!clientSecret && isEdit) || isCreatingIntent}>
        <ModalComponent
          showClose
          renderHeader={
            <>
              {!isCreatingIntent ? (
                <div className="text-darkBlue text-xl font-semibold">Card Details</div>
              ) : null}
            </>
          }
          containerClass="md:w-[688px] md:h-[348px] h-[540px] w-[320px] !md:m-0 !p-0"
          onClose={onCancel}
        >
          {isCreatingIntent ? (
            <div className="flex justify-center item-center w-full">
              <Loader />
            </div>
          ) : (
            <Elements stripe={stripePromise} key={clientSecret}>
              <EditCardForm
                clientSecret={clientSecret}
                onCancel={onCancel}
                onSuccess={onSuccess}
                businessUuid={selectedBusiness?.uuid || ''}
              />
            </Elements>
          )}
        </ModalComponent>
      </Render>
    </div>
  );
};

export default CardManagement;
